import { LinearProgress, makeStyles, Typography } from '@material-ui/core';

import { CourseTheme } from 'assets/themes/Course';
import { useCourseProgress } from 'hooks/progress';
import { useAppSelector } from 'hooks/redux';
import { SvgIcon } from 'components/Shared/SvgIcon';
import React, { ReactElement } from 'react';

interface IClassParams {
  minimumPercent: number;
  reachedMinimum: boolean;
}

interface IProps {
  size: 'small' | 'large';
  children: ReactElement;
}

const useStyles = makeStyles<CourseTheme, IClassParams>((theme) => ({
  progressContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    '& *[data-size=large]': {
      '& svg': {
        top: 0,
        width: 24,
        height: 24,
        left: (props) => `calc(${props.minimumPercent}% - 21px)`,
      },
    },
    '& *[data-size=small]': {
      '& svg': {
        top: -2,
        width: 24,
        height: 24,
        left: (props) => `calc(${props.minimumPercent}% - 12px)`,
      },
      '& > div': {
        height: 4,
      },
    },
  },
  progressPercentLabel: {
    '&[data-size=small]': {
      '@media(max-width: 768px)': {
        fontSize: 8,
      },
      fontWeight: 700,
      fontSize: 10,
      '& > div': {
        height: 4,
      },
    },
    '@media(max-width: 768px)': {
      fontSize: 14,
    },
    fontWeight: 600,
    color: theme.variables.courseTheme.text,
  },
  progress: {
    '& > div > div': {
      borderRadius: 8,
      background: theme.palette.primary.light,
    },
    '& > div': {
      height: 8,
      borderRadius: 8,
      background: theme.palette.grey[600],
      margin: theme.spacing(2, 0),
    },
    marginBottom: theme.spacing(2),
    width: '100%',
    position: 'relative',
    '& svg': {
      position: 'absolute',
      zIndex: 1,
    },
  },
}));

const Progress = (props: IProps) => {
  const course = useAppSelector((state) => state.course);
  const modules = useAppSelector((state) => state.newModules.modules);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { progress, certificateUrl, registerCertificateEvent, reachedMinimum } = useCourseProgress(modules, course);
  const minimumPercent = course?.progress_percent;
  const classes = useStyles({
    minimumPercent: minimumPercent ?? 0,
    reachedMinimum,
  });

  if (!course.progress_bar || !isAuthenticated) {
    return null;
  }

  return (
    <div className={classes.progressContainer}>
      <Typography
        aria-label='Seu progresso no curso'
        id='informations-course-progress-title'
        className={classes.progressPercentLabel}
        data-size={props.size}
        variant='h4'
      >
        Curso {progress}% concluído
      </Typography>
      <div className={classes.progress} data-size={props.size}>
        {course?.progress_percent <= 100 && course?.has_certificate && (
          <SvgIcon
            aria-label={`Porcentagem mínima para emitir o certificado: ${minimumPercent}`}
            name={progress >= minimumPercent || progress === 100 ? 'start-circle' : 'start-circle-light'}
          />
        )}
        <LinearProgress id='informations-course-progress-bar' variant='determinate' value={progress} />
      </div>
      {certificateUrl &&
        React.cloneElement(props.children, {
          onClick: registerCertificateEvent,
        })}
    </div>
  );
};

export default Progress;
